import { Box, SvgIcon, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import useScreenSize from '../../../utils/useScreenSize';
import AnimatedEllipsis from '../shared/Chat/Messages/AnimatedEllipsis';
import AIAssistantEditorTopBar from '../shared/TextEditor/AIAssistantEditorTopBar';

const ICON_SIZE = 48;
const ICON_CONTAINER_SIZE = 120;

const wrapperStyles = {
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	flex: 1,
	minHeight: 0,
};

const containerStyles = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'flex-start',
	width: '100%',
	flex: 1,
	padding: `${spaces.xxxxl} ${spaces.xl} ${spaces.xl} ${spaces.xl}`,
	boxSizing: 'border-box',
};

const iconContainerStyles = {
	width: ICON_CONTAINER_SIZE,
	height: ICON_CONTAINER_SIZE,
	borderRadius: '50%',
	backgroundColor: palette.primary.main,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	mb: spaces.lg,
};

const headingStyles = {
	color: palette.text.primary,
	mb: spaces.sm,
};

const stepsContainerStyles = {
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: spaces.xxs,
	mt: spaces.md,
};

const stepStyles = {
	color: palette.text.secondary,
};

const PodcastLoadingView: React.FC = () => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isSmallScreen = isXsScreen || isSmScreen;

	return (
		<Box sx={wrapperStyles}>
			{!isSmallScreen && <AIAssistantEditorTopBar isPodcastLoading />}
			<Box
				sx={containerStyles}
				data-testid="podcast-loading-view"
				role="status"
				aria-live="polite"
				aria-busy="true"
			>
				<Box sx={iconContainerStyles} aria-hidden="true">
					<SvgIcon
						component={getMuiIcon('microphone2')}
						inheritViewBox
						sx={{
							fontSize: ICON_SIZE,
							'& path': { fill: palette.primary.contrastText },
						}}
					/>
				</Box>

				<Typography variant="h5" sx={headingStyles}>
					{t('Podcast is being created')}
					<AnimatedEllipsis />
				</Typography>

				<Box sx={stepsContainerStyles}>
					{['Analyze content', 'Create script', 'Generate audio'].map((step) => (
						<Typography key={step} variant="body2" sx={stepStyles}>
							{'• '}
							{t(step)}
						</Typography>
					))}
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						mt: spaces.md,
						color: palette.text.secondary,
					}}
				>
					<SvgIcon
						component={getMuiIcon('infoCircle')}
						sx={{ color: 'inherit', width: 24, height: 24 }}
						viewBox="0 0 24 24"
					/>
					<Typography variant="body2" sx={{ color: 'inherit' }}>
						{t("You don't have to wait, you can find your finished podcast anytime under “My Contents”.")}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default PodcastLoadingView;
